import { Component, OnInit, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Step } from '../stepper/step';
import { StepsService } from '../../services/steps.service';
import { LinkUsersService } from '../../services/link-user.service';
import { ISendSmsOtpResponse, ILinkUsersResponse } from '../../classes/http-responses';
import { ICodeService } from '../../services/icode.service';
import { LoginService } from '../../services/login.service';
import { IErrorDescription } from '@ibank-globals/error';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { finalize, take } from 'rxjs/operators';
import { LoginAction, OTPReason } from '../../classes/static/enums';
import { Extensions } from '../../classes/static/extensions';
import { UserData } from '../../../globals/classes/userData';
import { BaseComponent } from '../../../globals/classes/base.component';
import { UserAuth } from '../../classes/userAuth';
import { AppSettings } from '../../app.settings';

@Component({
	selector: 'connect-profile-modal',
	template: require('./connect-profile-modal.component.html'),
})
export class ConnectProfileModalComponent extends BaseComponent implements OnInit, AfterViewInit {
	@Input() isRenew: boolean;
	@Input() targetUserId: string;

	public title: string;
	public steps: Step[];

	public loading: boolean;
	public isGroupUserIdsBigModalEnabled: boolean = AppSettings.isGroupUserIdsBigModalEnabled;

	private icode: string;
	private isCorporate: boolean;
	public isRebrading = AppSettings.isRebrading;

	constructor(
		private activeModalService: NgbActiveModal,
		public stepsService: StepsService,
		private icodeService: ICodeService
	) {
		super();
	}

	ngOnInit(): void {
		this.steps = this.stepsService.getTransactionSteps('ConnectProfileModal');
		this.stepsService.setStep(this.steps, 0);
		this.initateSubscriptions();
	}

	ngAfterViewInit(): void {
		this.checkIfIsConnect();
	}

	closeModal(): void {
		LoginService.loginAction$
			.pipe(take(1))
			.subscribe((action) =>
				action === LoginAction.RenewLink
					? (window.location.href = Extensions.returnUrl)
					: this.activeModalService.close()
			);
	}

	proceed(): void {
		if (!this.canProceed) return;
		switch (this.stepsService.getCurrentStep) {
			case 0:
				this.verifyTargetOTP(this.icode);
				break;
			case 1:
				this.verifyInitiatorOTP(this.icode);
				break;
		}
	}

	goToProfile(): void {
		if (this.stepsService.getCurrentStep !== 2) return;
		this.finalize();
	}

	get canProceed(): boolean {
		switch (this.stepsService.getCurrentStep) {
			case 0:
			case 1:
				return this.isValidIcode;
			default:
				return true;
		}
	}

	get proceedText(): string {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				return 'Συνέχεια';
			default:
				return 'Επιβεβαίωση';
		}
	}

	get isLastStep(): boolean {
		return this.stepsService.getCurrentStep === this.steps.length - 1;
	}

	private checkIfIsConnect(): void {
		setTimeout(() => {
			if (this.isRenew) {
				this.initiateLinkRenewal();
				this.title = 'Ανανέωση εξουσιοδότησης';
			} else {
				this.title = 'Σύνδεση προφίλ';
			}
		});
	}

	private initateSubscriptions(): void {
		this.initiateICodeSubs();
		this.mortalize(UserData.isCorporate$).subscribe((isCorp) => (this.isCorporate = isCorp));
		this.mortalize(UserData.data$).subscribe(
			(userData) => !this.isRenew && (this.targetUserId = userData.userId)
		);
	}

	private initiateICodeSubs() {
		this.mortalize(this.icodeService.code$).subscribe((code) => this.codeValueUpdated(code));
		this.mortalize(this.icodeService.codeRequested$).subscribe(() => this.codeRequested());
		this.mortalize(this.icodeService.codeSubmitted$).subscribe(() => this.proceed());
	}

	public moveBack(steps: Step[]): void {
		this.stepsService.moveBackStep(steps);
		//this.initiateICodeSubs();
	}

	private codeRequested(): void {
		if (!this.isRenew) {
			this.codeRequestedCreateLink();
		} else {
			this.codeRequestedRenewLink();
		}
	}

	private codeRequestedCreateLink(): void {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				UserAuth.requestOtp(this.targetUserId, OTPReason.LinkUser).subscribe(
					(response) => response.succeeded && this.icodeService.resetTimer()
				);
				break;
			case 1:
				UserAuth.requestOtp(null, OTPReason.LinkUser).subscribe(
					(response) => response.succeeded && this.icodeService.resetTimer()
				);
				break;
		}
	}

	private codeRequestedRenewLink(): void {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				UserAuth.requestOtp(this.targetUserId, OTPReason.RenewLink).subscribe(
					(response) => response.succeeded && this.icodeService.resetTimer()
				);
				break;
			case 1:
				UserAuth.requestOtp(null, OTPReason.RenewLink).subscribe(
					(response) => response.succeeded && this.icodeService.resetTimer()
				);
				break;
		}
	}

	private codeValueUpdated(code: string): void {
		this.icode = code;
	}

	private get isValidIcode(): boolean {
		return this.icodeService.isValidCode(this.icode);
	}

	private initiateLinkRenewal(): void {
		this.loading = true;
		LinkUsersService.initiateLinkRenewal(this.targetUserId)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(response) => {},
				(error) => this.handleInitializeError(error)
			);
	}

	private handleInitializeError(error: IErrorDescription): void {
		this.activeModalService.close();
		ErrorHandler.newError(error);
	}

	private verifyTargetOTP(otp: string): void {
		this.loading = true;
		LinkUsersService.verifyTargetOtp(otp)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(response) => this.handleVerifyTargetOTPResponse(response),
				(error) => this.handleFlowError(error)
			);
	}

	private verifyInitiatorOTP(otp: string) {
		this.loading = true;
		LinkUsersService.verifyInitiatorOtp(otp)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(response) => this.handleVerifyInitiatorOTPResponse(response),
				(error) => this.handleFlowError(error)
			);
	}

	private handleVerifyTargetOTPResponse(response: ISendSmsOtpResponse): void {
		this.loading = false;
		if (response.succeeded) {
			this.stepsService.moveNextStep(this.steps);
		}
	}

	private handleVerifyInitiatorOTPResponse(response: ILinkUsersResponse): void {
		if (response) {
			this.stepsService.moveNextStep(this.steps);
		}
	}

	private handleFlowError(error: IErrorDescription): void {
		if (!error) {
			ErrorHandler.newError(null);
			return;
		}

		var errorCode = parseInt(error.code);
		if (Extensions.isLinkUserErrorCode(errorCode)) this.activeModalService.close();
		ErrorHandler.newError(error);
	}

	private finalize(): void {
		this.loading = true;
		LinkUsersService.completeUserLink()
			.pipe(finalize(() => (this.loading = false)))
			.subscribe();
	}
}
